/*!
 * @copyright
 * Copyright(c) 2022 SICK AG
 */
import { numberGuard } from './number-utils';

function computeSeparators(locale) {
  const separators = {
    decimalSeparator: '.',
    thousandSeparator: ','
  };
  const numberFormat = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    useGrouping: true
  });
  const formattedValue = numberFormat.format(1234.5); // sanity check

  if (formattedValue.indexOf('1') === -1) {
    // return defaults
    return separators;
  }

  separators.decimalSeparator = formattedValue.replace(/.*4(.*)5.*/, '$1');
  separators.thousandSeparator = formattedValue.replace(/.*1(.*)2.*/, '$1');
  return separators;
}

const POSITIVE_INFINITY_STRING = '∞';
const NEGATIVE_INFINITY_STRING = '-∞';
/**
 * Parses the given string-value and returns a number.
 */

export function parseNumber(value, separators) {
  if (value === POSITIVE_INFINITY_STRING) {
    return Number.POSITIVE_INFINITY;
  }

  if (value === NEGATIVE_INFINITY_STRING) {
    return Number.NEGATIVE_INFINITY;
  }

  const replaceRegExp = new RegExp(`[^-0-9${separators.decimalSeparator}]`, 'g');
  value = value.replace(replaceRegExp, '').trim();

  if (separators.decimalSeparator !== '.') {
    value = value.replace(separators.decimalSeparator, '.');
  }

  return parseFloat(value);
}
/**
 * Format the given number with a Intl formatter. Handle edgecases NaN, undefined, null, -∞, ∞
 */

export function toString(numberFormat, value) {
  const numberValue = numberGuard(value);

  if (isNaN(numberValue)) {
    return '';
  }

  if (numberValue === Number.POSITIVE_INFINITY) {
    return POSITIVE_INFINITY_STRING;
  }

  if (numberValue === Number.NEGATIVE_INFINITY) {
    return NEGATIVE_INFINITY_STRING;
  }

  return numberFormat.format(numberValue);
}
/**
 * A converter for numbers and strings.
 */

/**
 * A {@link NumberConverter} for the locale of the host.
 */
export const defaultNumberConverter = {
  toNumber(value) {
    return parseNumber(value, computeSeparators());
  },

  toString(value) {
    return toString(new Intl.NumberFormat(undefined, {
      maximumSignificantDigits: 20
    }), value);
  }

};
/**
 * A {@link NumberConverter} for the 'en-US' locale.
 */

export const usNumberConverter = {
  toNumber(value) {
    return parseNumber(value, computeSeparators('en-US'));
  },

  toString(value) {
    return toString(new Intl.NumberFormat('en-US', {
      maximumSignificantDigits: 20
    }), value);
  }

};
